import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  alpha,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  SmartToy as AIIcon,
  Article as ArticleIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import api from '../../services/api';

const AINewsImpactDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [aiData, setAiData] = useState({
    totalIndicators: 0,
    aiAdjustedCount: 0,
    totalArticles: 0,
    sentimentDistribution: [],
    topImpacts: [],
    avgAdjustment: 0,
  });

  const fetchAIData = useCallback(async () => {
    try {
      // Fetch AI news health and macro scores
      const [newsHealth, macroScores] = await Promise.all([
        api.get('/ai-news/health'),
        api.get('/macro/indicators'),
      ]);

      const aiHealthData = newsHealth.data;
      const indicators = macroScores.data || [];

      // Count AI-adjusted indicators
      const aiAdjusted = indicators.filter(ind => ind.uses_ai_adjustment);
      const avgAdj = aiAdjusted.reduce((sum, ind) => 
        sum + Math.abs(ind.ai_adjustment_factor || 0), 0
      ) / (aiAdjusted.length || 1);

      // Sentiment distribution
      const sentimentDist = [
        { name: t('aiNews.sentiment.positive'), value: aiHealthData.positive_news || 0, color: '#11998e' },
        { name: t('aiNews.sentiment.neutral'), value: aiHealthData.neutral_news || 0, color: '#ffa726' },
        { name: t('aiNews.sentiment.negative'), value: aiHealthData.negative_news || 0, color: '#ff6b6b' },
      ];

      // Top impacts (sorted by adjustment magnitude)
      const topImpacts = aiAdjusted
        .sort((a, b) => Math.abs(b.ai_adjustment_factor) - Math.abs(a.ai_adjustment_factor))
        .slice(0, 5)
        .map(ind => ({
          name: ind.indicator_name,
          adjustment: ind.ai_adjustment_factor * 100,
          baseScore: ind.base_score,
          adjustedScore: ind.ai_adjusted_score,
        }));

      setAiData({
        totalIndicators: indicators.length,
        aiAdjustedCount: aiAdjusted.length,
        totalArticles: aiHealthData.total_articles || 0,
        sentimentDistribution: sentimentDist,
        topImpacts,
        avgAdjustment: avgAdj * 100,
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching AI data:', error);
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAIData();
  }, [fetchAIData]);

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper', boxShadow: 'none' }, transition: 'none' }}>
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  const aiCoverage = (aiData.aiAdjustedCount / aiData.totalIndicators) * 100;

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        mb: 3,
        overflow: 'visible',
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'background.paper',
          boxShadow: 'none',
        },
        transition: 'none',
      }}
    >
      {/* Header with gradient - Compact */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AIIcon sx={{ fontSize: 28, mr: 1.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('dashboard.aiImpact.title')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {t('dashboard.aiImpact.subtitle')}
            </Typography>
          </Box>
          <Chip
            icon={<SpeedIcon />}
            label={t('dashboard.aiImpact.active')}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              backdropFilter: 'blur(10px)',
            }}
          />
        </Box>
      </Box>

      <CardContent sx={{ p: 2 }}>
        {/* Summary Stats - Full Width Flexbox */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          {/* Card 1: Articles */}
          <Box
            sx={{
              flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 25%' },
              textAlign: 'center',
              p: 1.5,
              bgcolor: alpha('#667eea', 0.05),
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha('#667eea', 0.1),
            }}
          >
            <ArticleIcon sx={{ fontSize: 32, color: '#667eea', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#667eea' }}>
              {aiData.totalArticles}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('dashboard.aiImpact.articlesAnalyzed')}
            </Typography>
          </Box>

          {/* Card 2: Indicators */}
          <Box
            sx={{
              flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 25%' },
              textAlign: 'center',
              p: 1.5,
              bgcolor: alpha('#11998e', 0.05),
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha('#11998e', 0.1),
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 32, color: '#11998e', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#11998e' }}>
              {aiData.aiAdjustedCount}<Typography component="span" variant="body2" color="text.secondary">/{aiData.totalIndicators}</Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('dashboard.aiImpact.indicatorsAdjusted')}
            </Typography>
          </Box>

          {/* Card 3: Coverage */}
          <Box
            sx={{
              flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 25%' },
              textAlign: 'center',
              p: 1.5,
              bgcolor: alpha('#764ba2', 0.05),
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha('#764ba2', 0.1),
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                width: 80,
                height: 80,
                mb: 0.5,
              }}
            >
              <svg width="80" height="80">
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  fill="none"
                  stroke={alpha('#764ba2', 0.1)}
                  strokeWidth="10"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="35"
                  fill="none"
                  stroke="#764ba2"
                  strokeWidth="10"
                  strokeDasharray={`${(aiCoverage / 100) * 220} 220`}
                  strokeLinecap="round"
                  transform="rotate(-90 40 40)"
                />
              </svg>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#764ba2', fontSize: '1.25rem' }}>
                  {aiCoverage.toFixed(0)}%
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
              {t('dashboard.aiImpact.coverage')}
            </Typography>
          </Box>

          {/* Card 4: Avg Adjustment */}
          <Box
            sx={{
              flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 25%' },
              textAlign: 'center',
              p: 1.5,
              bgcolor: alpha('#ffa726', 0.05),
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha('#ffa726', 0.1),
            }}
          >
            <SpeedIcon sx={{ fontSize: 32, color: '#ffa726', mb: 0.5 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffa726' }}>
              {aiData.avgAdjustment > 0 ? '+' : ''}{aiData.avgAdjustment.toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {t('dashboard.aiImpact.avgAdjustment')}
            </Typography>
          </Box>
        </Box>

        {/* Charts Row - Full Width */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              {/* Sentiment Distribution */}
              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: '0 0 30%' },
                  bgcolor: 'background.paper',
                  p: 2,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('dashboard.aiImpact.sentimentDistribution')}
                </Typography>
                
                {/* Legend */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {aiData.sentimentDistribution.map((entry, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, bgcolor: entry.color, borderRadius: 0.5 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {entry.name}: {entry.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <ResponsiveContainer width="100%" height={150} minWidth={300} minHeight={150}>
                  <PieChart>
                    <Pie
                      data={aiData.sentimentDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                      strokeWidth={2}
                      stroke="#fff"
                    >
                      {aiData.sentimentDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip cursor={false} wrapperStyle={{ zIndex: 1000 }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              {/* Top Impacts */}
              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: '1 1 70%' },
                  bgcolor: 'background.paper',
                  p: 2,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {t('dashboard.aiImpact.topImpacts')}
                </Typography>
                {aiData.topImpacts.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180} minWidth={300} minHeight={180}>
                    <BarChart data={aiData.topImpacts} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.05)} />
                      <XAxis type="number" style={{ fontSize: '0.7rem' }} />
                      <YAxis type="category" dataKey="name" width={120} style={{ fontSize: '0.7rem' }} />
                      <RechartsTooltip 
                        formatter={(value) => `${value > 0 ? '+' : ''}${value.toFixed(2)}%`}
                        contentStyle={{
                          backgroundColor: 'rgba(255,255,255,0.95)',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                        }}
                        cursor={false}
                        wrapperStyle={{ zIndex: 1000 }}
                      />
                      <Bar dataKey="adjustment" radius={[0, 6, 6, 0]} activeBar={false}>
                        {aiData.topImpacts.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.adjustment > 0 ? '#11998e' : '#ff6b6b'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box
                    sx={{
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body2">
                      {t('dashboard.aiImpact.noImpacts')}
                    </Typography>
                  </Box>
                )}
              </Box>
        </Box>

        {/* AI Insights - Full Width */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
            p: 1.5,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: alpha('#667eea', 0.2),
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea', display: 'block', mb: 0.5 }}>
            💡 {t('dashboard.aiImpact.insights')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('dashboard.aiImpact.insightsText', {
              count: aiData.aiAdjustedCount,
              total: aiData.totalIndicators,
              articles: aiData.totalArticles,
            })}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AINewsImpactDashboard;

